import { getDefaultLandingPathForMode } from '@/modules/auth/core/postAuthLanding';
import type { ExperienceMode } from '@/modules/account-context/core/accountContext';

export type ExperienceModeNavItemId =
  | 'dashboard'
  | 'book'
  | 'my_bookings'
  | 'manage_businesses'
  | 'businesses'
  | 'my_business'
  | 'sports_courts'
  | 'users'
  | 'bookings'
  | 'activity_logs'
  | 'pricing'
  | 'dev';

export type ExperienceModePageId = 'my_bookings';

interface ExperienceModePolicyInput {
  selectedMode: ExperienceMode;
  isSuperAdmin: boolean;
  isBusinessAdmin: boolean;
  isStaff: boolean;
  showDevMenu: boolean;
}

const NAV_PRIORITY: Record<ExperienceMode, ExperienceModeNavItemId[]> = {
  super_admin: [
    'dashboard',
    'manage_businesses',
    'pricing',
    'businesses',
    'my_business',
    'sports_courts',
    'users',
    'bookings',
    'activity_logs',
    'book',
    'dev',
  ],
  player: ['dashboard', 'book', 'my_bookings', 'dev'],
  business_admin: ['dashboard', 'manage_businesses', 'my_business', 'sports_courts', 'users', 'bookings', 'activity_logs', 'book', 'dev'],
  staff: ['dashboard', 'bookings', 'activity_logs', 'book', 'dev'],
};

const MODE_VISIBLE_ITEMS: Record<ExperienceMode, Set<ExperienceModeNavItemId>> = {
  super_admin: new Set([
    'dashboard',
    'book',
    'manage_businesses',
    'pricing',
    'businesses',
    'my_business',
    'sports_courts',
    'users',
    'bookings',
    'activity_logs',
    'dev',
  ]),
  player: new Set(['dashboard', 'book', 'my_bookings', 'dev']),
  business_admin: new Set([
    'dashboard',
    'book',
    'manage_businesses',
    'my_business',
    'sports_courts',
    'users',
    'bookings',
    'activity_logs',
    'dev',
  ]),
  staff: new Set([
    'dashboard',
    'book',
    'bookings',
    'activity_logs',
    'dev',
  ]),
};

export function createExperienceModePolicy(input: ExperienceModePolicyInput) {
  return {
    isNavItemVisible(itemId: ExperienceModeNavItemId) {
      if (itemId === 'dev') return input.showDevMenu;
      return MODE_VISIBLE_ITEMS[input.selectedMode].has(itemId);
    },

    sortNavItems<T extends { id: ExperienceModeNavItemId; label: string }>(items: T[]): T[] {
      const priority = NAV_PRIORITY[input.selectedMode];

      return [...items].sort((left, right) => {
        if (left.id === 'dev' && right.id !== 'dev') return 1;
        if (right.id === 'dev' && left.id !== 'dev') return -1;

        const leftIndex = priority.indexOf(left.id);
        const rightIndex = priority.indexOf(right.id);

        if (leftIndex === -1 && rightIndex === -1) {
          return left.label.localeCompare(right.label);
        }

        if (leftIndex === -1) return 1;
        if (rightIndex === -1) return -1;

        return leftIndex - rightIndex;
      });
    },

    getPageGuard(pageId: ExperienceModePageId) {
      if (pageId === 'my_bookings') {
        if (input.selectedMode === 'player') {
          return { allowed: true, redirectTo: null as string | null };
        }

        return { allowed: false, redirectTo: getDefaultLandingPathForMode(input.selectedMode) };
      }

      return { allowed: true, redirectTo: null as string | null };
    },

    canShowOperationalBookingTools(canBookOnBehalf: boolean) {
      return canBookOnBehalf && input.selectedMode !== 'player';
    },
  };
}

